import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, Search, BarChart3, ChevronLeft, ChevronRight, Brain, Target } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { WLLSPredictionService } from '@/services/WLLSPredictionService';

interface WLLSResultsTabProps {
  fileId: string;
}

// Check if analysis was done in binary mode by examining pattern strings
const isBinaryMode = (patterns: any[]) => {
  if (!patterns || patterns.length === 0) return false;
  // Binary mode: patterns don't contain 'L' or 'S' (just numbers)
  // Side-aware mode: patterns contain 'L' or 'S'
  return patterns.some(p => p.pattern && !p.pattern.includes('L') && !p.pattern.includes('S'));
};

export const WLLSResultsTab: React.FC<WLLSResultsTabProps> = ({ fileId }) => {
  const [searchPattern, setSearchPattern] = useState('');
  const [predictionInput, setPredictionInput] = useState('');
  const [prediction, setPrediction] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [useRealisticPrediction, setUseRealisticPrediction] = useState(false);
  const [isPredictionLoading, setIsPredictionLoading] = useState(false);
  const pageSize = 100;

  const { data: file } = useQuery({
    queryKey: ['wlls-file-results', fileId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wlls_files')
        .select('filename, analysis_status, total_trades')
        .eq('id', fileId)
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  const { data: patternsData, isLoading } = useQuery({
    queryKey: ['wlls-patterns-paginated', fileId, searchPattern, currentPage],
    queryFn: async () => {
      const offset = (currentPage - 1) * pageSize;
      
      let query = supabase
        .from('wlls_patterns')
        .select('*', { count: 'exact' })
        .eq('file_id', fileId)
        .order('confidence_score', { ascending: false });

      if (searchPattern) {
        query = query.ilike('pattern', `%${searchPattern}%`);
      }

      const { data, error, count } = await query
        .range(offset, offset + pageSize - 1);
      
      if (error) throw error;
      const patterns = data || [];
      const binaryMode = isBinaryMode(patterns);
      return { patterns, totalCount: count || 0, binaryMode };
    }
  });

  const { data: topPatterns } = useQuery({
    queryKey: ['wlls-patterns-top', fileId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wlls_patterns')
        .select('*')
        .eq('file_id', fileId)
        .order('confidence_score', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      const patterns = data || [];
      const binaryMode = isBinaryMode(patterns);
      return { patterns, binaryMode };
    }
  });

  const handlePredict = async () => {
    if (!predictionInput.trim()) return;
    
    setIsPredictionLoading(true);
    
    try {
      if (useRealisticPrediction) {
        // Use realistic prediction (honest engine style)
        const result = await WLLSPredictionService.makeRealisticPrediction(
          fileId,
          predictionInput,
          0.52
        );
        
        setPrediction({
          ...result,
          mode: 'realistic',
          message: result.skipReason || undefined
        });
      } else {
        // Use suffix-based prediction (current logic)
        handleSuffixPrediction();
      }
    } catch (error) {
      console.error('Prediction error:', error);
      setPrediction({
        message: error instanceof Error ? error.message : 'Prediction failed',
        confidence: 0
      });
    } finally {
      setIsPredictionLoading(false);
    }
  };

  const handleSuffixPrediction = () => {
    if (!topPatterns?.patterns) return;

    const matchingPatterns = topPatterns.patterns.filter(pattern =>
      predictionInput.endsWith(pattern.pattern)
    );

    if (matchingPatterns.length === 0) {
      setPrediction({
        message: 'No matching patterns found',
        confidence: 0,
        mode: 'suffix'
      });
      return;
    }

    // Calculate weighted prediction
    let totalWinLong = 0, totalLossLong = 0, totalWinShort = 0, totalLossShort = 0;
    let totalWeight = 0;

    matchingPatterns.forEach(pattern => {
      const weight = pattern.confidence_score;
      totalWinLong += pattern.next_win_long * weight;
      totalLossLong += pattern.next_loss_long * weight;
      totalWinShort += pattern.next_win_short * weight;
      totalLossShort += pattern.next_loss_short * weight;
      totalWeight += weight * (pattern.next_win_long + pattern.next_loss_long + pattern.next_win_short + pattern.next_loss_short);
    });

    const totalNext = totalWinLong + totalLossLong + totalWinShort + totalLossShort;
    if (totalNext === 0) {
      setPrediction({ message: 'No next trade data available', confidence: 0, mode: 'suffix' });
      return;
    }

    const winProb = ((totalWinLong + totalWinShort) / totalNext) * 100;
    const longProb = ((totalWinLong + totalLossLong) / totalNext) * 100;
    const avgConfidence = totalWeight / totalNext;

    setPrediction({
      winProbability: Math.round(winProb * 100) / 100,
      longProbability: Math.round(longProb * 100) / 100,
      confidence: Math.round(avgConfidence * 100) / 100,
      matchingPatterns: matchingPatterns.length,
      bestNext: getBestNext(totalWinLong, totalLossLong, totalWinShort, totalLossShort),
      mode: 'suffix'
    });
  };

  const getBestNext = (winLong: number, lossLong: number, winShort: number, lossShort: number) => {
    const options = [
      { token: '1L', count: winLong, label: 'Win Long' },
      { token: '0L', count: lossLong, label: 'Loss Long' },
      { token: '1S', count: winShort, label: 'Win Short' },
      { token: '0S', count: lossShort, label: 'Loss Short' }
    ];
    
    return options.reduce((best, current) => 
      current.count > best.count ? current : best
    );
  };

  const getConfidenceBadge = (score: number) => {
    if (score >= 80) return <Badge className="bg-green-100 text-green-800">Very High</Badge>;
    if (score >= 65) return <Badge className="bg-blue-100 text-blue-800">High</Badge>;
    if (score >= 50) return <Badge className="bg-yellow-100 text-yellow-800">Medium</Badge>;
    return <Badge className="bg-gray-100 text-gray-800">Low</Badge>;
  };

  const totalPages = patternsData ? Math.ceil(patternsData.totalCount / pageSize) : 0;

  const handleSearchChange = (value: string) => {
    setSearchPattern(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  if (!file) return null;

  return (
    <div className="space-y-6">
      {/* File Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Analysis Results - {file.filename}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Status: </span>
              <Badge className={
                file.analysis_status === 'completed' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }>
                {file.analysis_status}
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Total Trades: </span>
              {file.total_trades || 0}
            </div>
            <div>
              <span className="text-muted-foreground">Total Patterns: </span>
              {patternsData?.totalCount || 0}
            </div>
          </div>
          
          {/* Analysis Mode Indicator */}
          {patternsData?.binaryMode !== undefined && (
            <div className="mt-2">
              <Badge variant="outline" className={patternsData.binaryMode ? "border-blue-200 text-blue-800" : "border-green-200 text-green-800"}>
                Mode: {patternsData.binaryMode ? "Binary (Honest Engine Compatible)" : "Side-aware (4-outcome)"}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prediction Tool */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {useRealisticPrediction ? <Brain className="h-5 w-5" /> : <Target className="h-5 w-5" />}
            Pattern Prediction
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Prediction Mode Toggle */}
          <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
            <Switch
              id="prediction-mode"
              checked={useRealisticPrediction}
              onCheckedChange={setUseRealisticPrediction}
            />
            <Label htmlFor="prediction-mode" className="flex items-center gap-2 cursor-pointer">
              {useRealisticPrediction ? (
                <>
                  <Brain className="h-4 w-4" />
                  Realistic (Honest Engine Style)
                </>
              ) : (
                <>
                  <Target className="h-4 w-4" />
                  Suffix-based Matching
                </>
              )}
            </Label>
            <Badge variant="outline" className="ml-2">
              {useRealisticPrediction ? 'Advanced' : 'Simple'}
            </Badge>
          </div>
          
          <div className="flex gap-2">
            <Input
              placeholder={patternsData?.binaryMode ? "Enter sequence (e.g., 101)" : "Enter sequence (e.g., 1S0L1S)"}
              value={predictionInput}
              onChange={(e) => setPredictionInput(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handlePredict} disabled={isPredictionLoading}>
              {isPredictionLoading ? (
                <>Loading...</>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Predict
                </>
              )}
            </Button>
          </div>
          
          {prediction && (
            <Card className="bg-muted/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant={prediction.mode === 'realistic' ? 'default' : 'secondary'}>
                    {prediction.mode === 'realistic' ? 'Realistic Prediction' : 'Suffix-based Prediction'}
                  </Badge>
                  {prediction.prediction && (
                    <Badge 
                      variant={
                        prediction.prediction === 'WIN' ? 'default' : 
                        prediction.prediction === 'LOSS' ? 'destructive' : 
                        'outline'
                      }
                      className="ml-2"
                    >
                      {prediction.prediction}
                    </Badge>
                  )}
                </div>
                
                {prediction.message ? (
                  <p className="text-muted-foreground">{prediction.message}</p>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p><span className="font-medium">Win Probability:</span> {prediction.winProbability?.toFixed(2)}%</p>
                        {prediction.lossProbability && (
                          <p><span className="font-medium">Loss Probability:</span> {prediction.lossProbability.toFixed(2)}%</p>
                        )}
                        {!patternsData?.binaryMode && prediction.longProbability && (
                          <p><span className="font-medium">Long Probability:</span> {prediction.longProbability}%</p>
                        )}
                      </div>
                      <div>
                        <p><span className="font-medium">Confidence:</span> {
                          typeof prediction.confidence === 'number' 
                            ? prediction.confidence.toFixed(1)
                            : prediction.confidence
                        }</p>
                        {prediction.bestNext && (
                          <p><span className="font-medium">Best Next:</span> {prediction.bestNext.token} ({prediction.bestNext.label})</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-muted-foreground text-sm">
                      {prediction.mode === 'realistic' ? (
                        <>
                          Based on {prediction.usedPatterns || 0} patterns using enhanced confidence weighting
                          {prediction.patternsUsed && prediction.patternsUsed.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <p className="font-medium">Top patterns used:</p>
                              {prediction.patternsUsed.slice(0, 3).map((pattern: any, idx: number) => (
                                <div key={idx} className="text-xs ml-2">
                                  {pattern.pattern} (freq: {pattern.frequency}, success: {pattern.successRate.toFixed(1)}%)
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <>Based on {prediction.matchingPatterns || 0} matching patterns</>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Patterns Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>WLLS Patterns</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search patterns..."
                  value={searchPattern}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading patterns...</div>
          ) : !patternsData?.patterns.length ? (
            <div className="text-center py-8 text-muted-foreground">
              No patterns found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pattern</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Next Counts</TableHead>
                      <TableHead>Win Rate</TableHead>
                      {!patternsData.binaryMode && <TableHead>Long Rate</TableHead>}
                      <TableHead>Confidence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patternsData.patterns.map((pattern) => (
                      <TableRow key={pattern.id}>
                        <TableCell className="font-mono">{pattern.pattern}</TableCell>
                        <TableCell>{pattern.frequency}</TableCell>
                        <TableCell className="text-xs">
                          {patternsData.binaryMode ? (
                            <div className="space-y-1">
                              <div>Win: {pattern.next_win_long}, Loss: {pattern.next_loss_long}</div>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div>1L: {pattern.next_win_long}, 0L: {pattern.next_loss_long}</div>
                              <div>1S: {pattern.next_win_short}, 0S: {pattern.next_loss_short}</div>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const totalNext = pattern.next_win_long + pattern.next_loss_long + pattern.next_win_short + pattern.next_loss_short;
                            return totalNext > 0 ? `${pattern.win_rate}%` : '—';
                          })()}
                        </TableCell>
                        {!patternsData.binaryMode && <TableCell>{pattern.long_rate}%</TableCell>}
                        <TableCell>{getConfidenceBadge(pattern.confidence_score)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, patternsData.totalCount)} of {patternsData.totalCount} patterns
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};